import type { ReactNode } from "react";
import { Flex, Popover } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";
import { Typography } from "@av/components/atoms/typography/Typography";
import {
  type AdminSettingsUserBase,
  type SecurityGroup,
} from "../../../../../services/admin-settings/type";

import type { TFunction } from "i18next";

type SecurityGroupsProps<TUser extends AdminSettingsUserBase> = {
  securityGroups: SecurityGroup[];
  productCode: string;
  user: TUser;
  t: TFunction;
  testIdPrefix: string;
  getTestIdValue?: (user: TUser) => string;
  onSecurityGroupClick?: (securityGroup: string, user: TUser) => void;
};

type SecurityGroupCellProps<TUser extends AdminSettingsUserBase> = SecurityGroupsProps<TUser> & {
  // Rendered at the right edge of the cell, e.g. the ATS Users email status indicator
  suffix?: ReactNode;
};

const useStyles = createStyles(({ css, token }) => ({
  securityGroupCountBadge: css`
    align-items: center;
    background: ${token.colorPrimary};
    border: ${token.lineWidth}px solid ${token.colorBgContainer};
    border-radius: 12px;
    box-sizing: border-box;
    color: ${token.colorBgContainer};
    display: inline-flex;
    font-size: ${token.fontSizeSM}px;
    height: 18px;
    justify-content: center;
    line-height: 1;
    min-width: 24px;
    padding: 0 ${token.paddingXXS}px;
    width: max-content;
  `,
  securityGroupLink: css`
    && {
      height: auto;
      padding: 0;
      white-space: normal;
    }
  `,
  securityGroupText: css`
    color: ${token.colorPrimary};
  `,
}));

export function SecurityGroupCell<TUser extends AdminSettingsUserBase>({
  suffix,
  ...securityGroupsProps
}: SecurityGroupCellProps<TUser>) {
  if (!suffix) {
    return <SecurityGroups {...securityGroupsProps} />;
  }

  return (
    <Flex align="center" gap={8} justify="space-between">
      <SecurityGroups {...securityGroupsProps} />
      {suffix}
    </Flex>
  );
}

function SecurityGroups<TUser extends AdminSettingsUserBase>({
  securityGroups,
  user,
  t,
  productCode,
  testIdPrefix,
  getTestIdValue = (adminSettingsUser) => adminSettingsUser.userId,
  onSecurityGroupClick,
}: SecurityGroupsProps<TUser>) {
  const { styles } = useStyles();
  const testIdValue = getTestIdValue(user);
  const securityGroupNames = securityGroups.map((securityGroup) => securityGroup.name);
  const [primarySecurityGroup, ...additionalSecurityGroups] = securityGroupNames;

  if (!primarySecurityGroup) {
    return (
      <Typography.Text testIdPrefix={`${testIdPrefix}-security-groups-empty-${testIdValue}`}>
        -
      </Typography.Text>
    );
  }

  const securityGroupContent = (
    <Flex
      align="center"
      aria-label={securityGroupNames.join(", ")}
      gap={8}
      tabIndex={additionalSecurityGroups.length > 0 ? 0 : undefined}
    >
      {onSecurityGroupClick ? (
        <Button
          type="link"
          className={styles.securityGroupLink}
          testIdPrefix={`${testIdPrefix}-security-group-${testIdValue}`}
          onClick={() => onSecurityGroupClick(primarySecurityGroup, user)}
        >
          {primarySecurityGroup}
        </Button>
      ) : (
        <Typography.Text
          className={styles.securityGroupText}
          testIdPrefix={`${testIdPrefix}-security-group-${testIdValue}`}
        >
          {primarySecurityGroup}
        </Typography.Text>
      )}
      {additionalSecurityGroups.length > 0 && (
        <span
          className={styles.securityGroupCountBadge}
          data-testid={`${testIdPrefix}-security-group-count-${testIdValue}`}
        >
          {`+${additionalSecurityGroups.length}`}
        </span>
      )}
    </Flex>
  );

  if (additionalSecurityGroups.length === 0) {
    return securityGroupContent;
  }

  const popoverTitle =
    productCode === "hr"
      ? t("platform.common.moduleName_hr")
      : t("platform.common.moduleName_recruitment");

  return (
    <Popover
      data-testid={`${testIdPrefix}-security-group-tooltip-${testIdValue}`}
      title={popoverTitle}
      content={<span style={{ whiteSpace: "pre-line" }}>{securityGroupNames.join("\n")}</span>}
    >
      {securityGroupContent}
    </Popover>
  );
}
